import Phaser from "phaser";
import {
  SegmentedTabBar,
  SEGMENTED_TAB_BAR_EVENT,
  type TabBarStyleToken,
} from "@yjsgames/phaser-ui";
import type { TextDirection } from "@yjsgames/i18n";
import { FONT_FAMILY, TYPO, LOCK_ICON_KEY } from "./uiTokens.js";
import { PALETTE, PALETTE_NUM } from "../themes/palette.js";
import { t } from "../i18n/i18n.js";
import { TAB_ORDER, filterTabRows, type TabId } from "./tabVisibility.js";
import { deriveTabRows, tabIconKey, tabInactiveIconKey } from "./tabCatalog.js";
import { DEPTH } from "../objects/depthLayers.js";

// 설정은 탭바에서 분리해 우상단 톱니 버튼 오버레이로 처리한다 (지시문 §4). 던전/유물/일일상점 탭 추가.
// TabId/TAB_ORDER 정의 + 빌드별 탭 필터는 순수 모듈 tabVisibility.ts로 이동(Phaser 없이 테스트) — 호환 re-export.
export { TAB_ORDER, deriveEnabledTabSet, filterTabOrder, filterTabRows } from "./tabVisibility.js";
export type { TabId } from "./tabVisibility.js";

/**
 * 하단 탭바 배치 — SSoT는 tabCatalog.ts `bar.row/col` (신규 탭 = 카탈로그 1줄, 본 파일 무접촉).
 * 현재 배치: 행1=상점·도감·유물·모듈숙련(4칸) / 행2=전투·스텟·모듈합성·환생·던전(5칸 — 셀 폭 약간 좁음).
 * 일일상점은 상점 탭 내 서브탭(ShopContainerTabView) — 최상위 탭에서 제거됨.
 */
const TAB_ROWS: readonly (readonly TabId[])[] = deriveTabRows();

/** 하단 탭바 스타일 토큰 — fantasy-tabs PNG 9-slice 에셋 사용. */
const TAB_BAR_STYLE: TabBarStyleToken = {
  activeTextureKey: "tab_button_active_9slice",
  inactiveTextureKey: "tab_button_inactive_9slice",
  border: { left: 10, top: 10, right: 10, bottom: 10 },
  labelStyle: { fontFamily: FONT_FAMILY, fontSize: TYPO.caption, color: PALETTE.textSecondary },
  labelActiveStyle: { fontFamily: FONT_FAMILY, fontSize: TYPO.caption, color: PALETTE.textBright },
  inactiveAlpha: 0.78,
  activeLabelScale: 1.03,
};

/**
 * 하단 탭바 — 항상 보인다 (지시문 §7). @yjsgames/phaser-ui의 SegmentedTabBar 2개를 세로로 스택한다.
 * 각 행은 독립 SegmentedTabBar이고, setActive는 두 바 모두에 적용한다 — 활성 탭이 없는 행은
 * setActiveTab이 미존재 id를 받아 전 탭을 비활성으로 표시하므로, 정확히 한 행의 한 탭만 하이라이트된다.
 * 탭을 바꿔도 전투 필드는 탭 콘텐츠가 아니므로 자동 전투가 계속된다 (§23).
 *
 * `dir: "rtl"`이면 각 행의 탭 셀 순서가 좌우 미러링된다(SegmentedTabBar에 그대로 전달).
 */
export class BottomTabView {
  private readonly bars: readonly SegmentedTabBar<TabId>[];
  private readonly tabSizes = new Map<TabId, { width: number; height: number }>();

  /**
   * @param y 탭바 전체 세로 중심.
   * @param rowHeight 각 행 높이 (SegmentedTabBar는 top 기준이라 행별 top을 계산해 전달).
   * @param rowGap 행 간격 — safe-area 압축이 반영된 값(deriveShellLayout.tabBarRowGap)을 받는다.
   * @param dir 텍스트 방향 — RTL이면 탭 셀 순서 미러링. default "ltr".
   * @param enabled 활성 탭 집합(featureFlags 파생) — 비활성 탭은 행에서 제거되고 빈 행은 통째 생략.
   *   잔여 탭이 width를 균등 분배하므로 빈 칸 없음. default 전체(기존 호출처 무변경).
   */
  constructor(
    scene: Phaser.Scene,
    x: number,
    y: number,
    width: number,
    rowHeight: number,
    rowGap: number,
    onSelect: (tab: TabId) => void,
    dir: TextDirection = "ltr",
    enabled: ReadonlySet<TabId> = new Set(TAB_ORDER),
  ) {
    const rows = filterTabRows(TAB_ROWS, enabled);
    const totalHeight = rows.length * rowHeight + (rows.length - 1) * rowGap;
    const topY = y - totalHeight / 2; // 전체 중심 기준 첫 행의 top
    this.bars = rows.map((row, i) => {
      const rowTop = topY + i * (rowHeight + rowGap);
      for (const id of row) this.tabSizes.set(id, { width: width / row.length, height: rowHeight });
      const bar = new SegmentedTabBar<TabId>(scene, x, rowTop, {
        style: TAB_BAR_STYLE,
        width,
        height: rowHeight,
        tabs: row.map((id) => ({
          id,
          label: t(`tab.${id}`),
          iconKey: tabIconKey(id),
          activeIconKey: tabIconKey(id),
          inactiveIconKey: tabInactiveIconKey(id),
        })),
        // 전역 초기 활성 탭. 이 탭이 없는 행은 생성 즉시 전 탭 비활성으로 표시된다.
        activeId: "battle",
        dir,
      });
      bar.setDepth(DEPTH.hudBar);
      bar.on(SEGMENTED_TAB_BAR_EVENT.TabChange, (id: TabId) => onSelect(id));
      scene.add.existing(bar);
      return bar;
    });
  }

  /** 활성 탭 동기화 — 두 행 모두에 적용. 탭이 없는 행은 전 탭 비활성으로 갱신된다. */
  setActive(active: TabId): void {
    for (const bar of this.bars) bar.setActiveTab(active);
  }

  /** 언어 변경 — 탭 라벨만 재표시 (객체 재생성 0). */
  applyLanguage(): void {
    for (const bar of this.bars) {
      bar.setTabLabels((id) => t(`tab.${id}`));
    }
  }

  /** 방향 전환(RTL) — 각 행 SegmentedTabBar 셀 순서 미러(행 세로 위치는 dir 무관·불변). */
  relayout(dir: TextDirection): void {
    for (const bar of this.bars) bar.relayout(dir);
  }

  /**
   * 챕터 잠금 상태 일괄 적용 — requiredOf(id)가 숫자면 해당 탭에 자물쇠+요구 챕터 배지(잠금), null이면 해제.
   * 두 행 모두에 적용(해당 탭 없는 행은 no-op). GameScene이 bestChapter 기준 tabLock 판정을 주입한다.
   */
  setLocked(requiredOf: (id: TabId) => number | null): void {
    for (const id of TAB_ORDER) {
      const req = requiredOf(id);
      for (const bar of this.bars) {
        if (req != null) {
          bar.setTabLocked(id, true, {
            iconKey: LOCK_ICON_KEY,
            chapterLabel: String(req),
            tint: PALETTE_NUM.signalDanger,
          });
        } else {
          bar.setTabLocked(id, false);
        }
      }
    }
  }

  /** 잠긴 탭 클릭 거부 흔들림 — 두 행 모두에 적용(해당 탭 없는 행은 no-op). */
  shake(id: TabId): void {
    for (const bar of this.bars) bar.shakeTab(id, PALETTE_NUM.signalDanger);
  }

  /** 탭 알림 레드닷 표시/숨김 (예: 도감 새 해금) — 두 행 모두에 적용(해당 탭 없는 행은 no-op). */
  setRedDot(id: TabId, show: boolean): void {
    for (const bar of this.bars) bar.setTabDot(id, show, PALETTE_NUM.signalDanger);
  }

  /** 유물 탭 버튼 월드 좌표 (보상 fly-to 타겟). 유물 탭이 비활성(미존재)이면 null. */
  relicTabPos(): { x: number; y: number } | null {
    for (const bar of this.bars) {
      const pos = bar.tabWorldCenter("relic" as TabId);
      if (pos) return pos;
    }
    return null;
  }

  /** 특정 하단 탭 버튼의 월드 사각형. 가이드/튜토리얼 하이라이트가 버튼 전체를 감싼다. */
  tabRect(id: TabId): { x: number; y: number; width: number; height: number } | null {
    for (const bar of this.bars) {
      const center = bar.tabWorldCenter(id);
      const size = this.tabSizes.get(id);
      if (center && size) {
        return {
          x: center.x - size.width / 2,
          y: center.y - size.height / 2,
          width: size.width,
          height: size.height,
        };
      }
    }
    return null;
  }
}
